import React, { useEffect, useState, useRef } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { useApplicationStore } from 'hooks/application'
import Select from 'components/select'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import {
  BLOCK_GROUP_BY,
  COLUMN_TYPE_NAMES,
  CHART_TYPES,
  DATE_COLUMN_TYPES,
  CHART_AGGREGATES,
  CHART_DATE_AGGREGATES,
  NUMBER_COLUMN_TYPES
} from 'components/spreadsheet/constants/const'
import { ITableViewChart, IChartType, CHARTS_WITH_GROUPING, IChartAggregate } from 'types'
import Toggle from 'components/toggle'
import { IColumnTypes, sortDirections } from 'components/spreadsheet/types'
import { shouldShowLegend } from './helper'

const STACKED_OR_CLUSTERED = new Set([
  'stacked-bar',
  'stacked-horizontal-bar',
  'clustered-bar',
  'clustered-horizontal-bar'
])

const isStackedOrClustered = (t: string | null) => (t ? STACKED_OR_CLUSTERED.has(t) : false)

const MAX_SERIES = 10 // optional: keep charts readable

const isDateKind = (k: IColumnTypes) => DATE_COLUMN_TYPES.includes(k)
const isNumericKind = (k: IColumnTypes) => NUMBER_COLUMN_TYPES.includes(k)

const ChartMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const [chart, setChart] = useState<ITableViewChart>({
    chartType: null,
    showLegend: true,
    showValues: false,
    showPercentage: false,
    showTitle: false,
    showCardLabel: true,
    columnLabelId: '',
    columnValueIds: [],
    columnStackId: null,
    columnGanttStartDateId: null,
    columnGanttEndDateId: null,
    columnGanttDeadlineId: null,
    aggregate: null,
    sortAggregate: null,
    maxCategories: null
  })

  const { setSnackbarMessage } = useApplicationStore()
  const didInit = useRef(false)

  const getAggregate = (chart: ITableViewChart): IChartAggregate => {
    let aggregate: IChartAggregate = null
    if (!chart.chartType) {
      return null
    }
    if (chart.chartType !== 'gantt') {
      aggregate = 'count'
      if (chart.columnValueIds && chart.columnValueIds.length > 0) {
        const columns = spreadsheetData.viewDetails.columns.filter((col) => chart.columnValueIds.includes(col.publicId))
        const areAllNumeric = columns.every((col) => NUMBER_COLUMN_TYPES.includes(col.kind))
        const areAllDate = columns.length > 0 && columns.every((col) => DATE_COLUMN_TYPES.includes(col.kind))
        if (chart.aggregate) {
          const numericAggs: IChartAggregate[] = ['count', 'sum', 'avg']
          const dateAggs: IChartAggregate[] = ['count', 'earliest', 'latest']
          if (areAllNumeric && numericAggs.includes(chart.aggregate)) {
            aggregate = chart.aggregate
          } else if (areAllDate && chart.chartType === 'card' && dateAggs.includes(chart.aggregate)) {
            aggregate = chart.aggregate
          } else {
            aggregate = 'count'
          }
        }
      }
    }
    return aggregate
  }

  useEffect(() => {
    const initialChart =
      spreadsheetData.userConfiguration.chartSettings === null
        ? spreadsheetData.viewDetails.chartSettings === null
          ? {
              chartType: null,
              showLegend: true,
              showValues: false,
              showPercentage: false,
              showTitle: false,
              showCardLabel: true,
              columnLabelId: '',
              columnValueIds: [],
              columnStackId: null,
              columnGanttStartDateId: null,
              columnGanttEndDateId: null,
              columnGanttDeadlineId: null,
              aggregate: null,
              sortAggregate: null,
              maxCategories: null
            }
          : spreadsheetData.viewDetails.chartSettings
        : spreadsheetData.userConfiguration.chartSettings

    setChart({ ...initialChart, aggregate: getAggregate(initialChart) })
  }, [])

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      return
    }

    if (JSON.stringify(chart) === JSON.stringify(spreadsheetData.userConfiguration.chartSettings)) {
      return
    } else {
      setSpreadsheetData({
        type: 'ADD_CHART',
        chart: { ...chart, aggregate: getAggregate(chart) }
      })
    }
  }, [chart])

  const handleSelectChartType = (chartType: IChartType) => {
    if (!chart) return
    const showLegend = shouldShowLegend(chartType)
    let next: ITableViewChart = { ...chart, chartType, showLegend }

    // switching away from gantt clears gantt cols; switching to gantt clears value cols
    if (chartType === 'gantt') {
      next = { ...next, columnValueIds: [], columnStackId: null }
    } else if (chart.chartType === 'gantt') {
      next = { ...next, columnGanttStartDateId: null, columnGanttEndDateId: null, columnGanttDeadlineId: null }
    }

    // date aggregates (earliest/latest) only work for card charts — reset when switching away
    if (chartType !== 'card' && (next.aggregate === 'earliest' || next.aggregate === 'latest')) {
      next = { ...next, aggregate: 'count' }
    }

    // S-Curve: enforce single value and date kinds
    if (chartType === 's-curve') {
      // reduce to at most 1 value id
      const first = next.columnValueIds[0] ?? null
      next = { ...next, columnValueIds: first ? [first] : [] }
      // label must be date
      if (next.columnLabelId) {
        const col = spreadsheetData.viewDetails.columns.find((c) => c.publicId === next.columnLabelId)
        if (col && !isDateKind(col.kind)) {
          next.columnLabelId = ''
          setSnackbarMessage({ status: 'warning', message: 'Label must be a date column for S-Curve.' })
        }
      }
      // value must be date
      if (first) {
        const vc = spreadsheetData.viewDetails.columns.find((c) => c.publicId === first)
        if (vc && !isDateKind(vc.kind)) {
          next.columnValueIds = []
          setSnackbarMessage({ status: 'warning', message: 'Value must be a date column for S-Curve.' })
        }
      }
    }

    // Stacked/Clustered: allow only one value column
    if (isStackedOrClustered(chartType) && next.columnValueIds.length > 1) {
      next = { ...next, columnValueIds: [next.columnValueIds[0]] }
      setSnackbarMessage({
        status: 'info',
        message: 'Stacked/clustered charts currently support one value column. Kept the first one.'
      })
    }

    // Card: no label, stack, or gantt columns needed; default to showing values + in-tile
    // labels. Show Title stays off — on a card it prints the view name as a heading, and
    // a new card chart should look the same as it always has until the user asks for one.
    if (chartType === 'card') {
      next = {
        ...next,
        columnLabelId: null,
        columnStackId: null,
        columnGanttStartDateId: null,
        columnGanttEndDateId: null,
        columnGanttDeadlineId: null,
        showCardLabel: true,
        showValues: true
      }
    }

    setChart(next)
  }

  const handleSelectChartAggregate = (aggregate: IChartAggregate) => {
    if (!chart || chart.chartType === 'gantt') return

    const valueColumns = spreadsheetData.viewDetails.columns.filter((c) => chart.columnValueIds.includes(c.publicId))

    if ((aggregate === 'avg' || aggregate === 'sum') && valueColumns.length > 0) {
      const allNumeric = valueColumns.every((c) => isNumericKind(c.kind))
      if (!allNumeric) {
        setSnackbarMessage({
          status: 'error',
          message: `${aggregate === 'avg' ? 'Average' : 'Sum'} only works when every value column holds numbers. Choose number columns, or use Count.`
        })
        return
      }
    }

    if (aggregate === 'earliest' || aggregate === 'latest') {
      if (chart.chartType !== 'card') {
        setSnackbarMessage({
          status: 'error',
          message: `${aggregate === 'earliest' ? 'Earliest' : 'Latest'} is only available on card charts.`
        })
        return
      }
      if (valueColumns.length > 0) {
        const allDate = valueColumns.every((c) => isDateKind(c.kind))
        if (!allDate) {
          setSnackbarMessage({
            status: 'error',
            message: `${aggregate === 'earliest' ? 'Earliest' : 'Latest'} only works when every value column is a date column. Choose date columns, or use Count.`
          })
          return
        }
      }
    }

    setChart({ ...chart, aggregate })
  }

  const handleAddLabelColumn = (columnId: string) => {
    if (chart) {
      if (!columnId) {
        setChart({ ...chart, columnLabelId: null })
        return
      }
      const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
      if (column) {
        const columnKind = column.kind
        if (BLOCK_GROUP_BY.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't be used in charts.`
          })
          return
        }
      }
      setChart({ ...chart, columnLabelId: columnId })
    }
  }

  const handleAddValueColumn = (columnId: string) => {
    if (!chart) return
    const col = spreadsheetData.viewDetails.columns.find((c) => c.publicId === columnId)
    if (!col) return

    if (chart.columnLabelId && chart.columnLabelId === columnId) {
      setSnackbarMessage({
        status: 'error',
        message: "This column is the chart's label (X-axis) column, so it can't also be a value column."
      })
      return
    }

    // block certain kinds universally
    if (BLOCK_GROUP_BY.includes(col.kind)) {
      setSnackbarMessage({
        status: 'error',
        message: `${COLUMN_TYPE_NAMES[col.kind]} columns can't be used in charts.`
      })
      return
    }

    // S-Curve: value must be a date; allow only a single value
    if (chart.chartType === 's-curve') {
      if (!isDateKind(col.kind)) {
        setSnackbarMessage({
          status: 'error',
          message: 'S-curve charts need a date column as the value. Choose a Date or Date + Time column.'
        })
        return
      }
      const already = chart.columnValueIds[0] === columnId
      setChart({ ...chart, columnValueIds: already ? [] : [columnId], aggregate: 'count' })
      return
    }

    // Stacked/Clustered: allow only one value
    if (isStackedOrClustered(chart.chartType)) {
      const already = chart.columnValueIds.includes(columnId)
      if (already) {
        // toggle off
        setChart({ ...chart, columnValueIds: chart.columnValueIds.filter((id) => id !== columnId), aggregate: 'count' })
      } else if (chart.columnValueIds.length >= 1) {
        setSnackbarMessage({
          status: 'error',
          message: 'Stacked and clustered charts use one value column. Remove the current one to choose another.'
        })
      } else {
        setChart({ ...chart, columnValueIds: [columnId], aggregate: 'count' })
      }
      return
    }

    // General case: toggle + cap series
    const included = chart.columnValueIds.includes(columnId)
    let nextIds = included
      ? chart.columnValueIds.filter((id) => id !== columnId) // toggle off
      : [...chart.columnValueIds, columnId] // toggle on

    // dedupe (defensive)
    nextIds = nextIds.filter((v, i, a) => a.indexOf(v) === i)

    if (nextIds.length > MAX_SERIES) {
      setSnackbarMessage({
        status: 'warning',
        message: `Limited to ${MAX_SERIES} value series for readability.`
      })
      nextIds = nextIds.slice(0, MAX_SERIES)
    }

    setChart({ ...chart, columnValueIds: nextIds, aggregate: getAggregate({ ...chart, columnValueIds: nextIds }) })
  }

  const handleAddStackColumn = (columnId: string) => {
    if (chart) {
      const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
      if (column) {
        const columnKind = column.kind
        if (BLOCK_GROUP_BY.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't be used in charts.`
          })
          return
        }
      }
      setChart({ ...chart, columnStackId: columnId })
    }
  }

  const handleAddGanttStartColumn = (columnId: string) => {
    if (chart) {
      const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
      if (column) {
        const columnKind = column.kind
        if (BLOCK_GROUP_BY.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't be used in charts.`
          })
          return
        }
        if (!DATE_COLUMN_TYPES.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: 'Gantt charts need date columns. Choose a Date or Date + Time column.'
          })
          return
        }
      }
      setChart({ ...chart, columnGanttStartDateId: columnId })
    }
  }

  const handleAddGanttEndColumn = (columnId: string) => {
    if (chart) {
      const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
      if (column) {
        const columnKind = column.kind
        if (BLOCK_GROUP_BY.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't be used in charts.`
          })
          return
        }

        if (!DATE_COLUMN_TYPES.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: 'Gantt charts need date columns. Choose a Date or Date + Time column.'
          })
          return
        }
      }
      setChart({ ...chart, columnGanttEndDateId: columnId })
    }
  }

  const handleAddGanttDeadlineColumn = (columnId: string) => {
    if (chart) {
      if (!columnId) {
        setChart({ ...chart, columnGanttDeadlineId: null })
        return
      }
      const column = spreadsheetData.viewDetails.columns.find((column) => column.publicId === columnId)
      if (column) {
        const columnKind = column.kind
        if (BLOCK_GROUP_BY.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't be used in charts.`
          })
          return
        }

        if (!DATE_COLUMN_TYPES.includes(columnKind)) {
          setSnackbarMessage({
            status: 'error',
            message: 'Gantt charts need date columns. Choose a Date or Date + Time column.'
          })
          return
        }
      }
      setChart({ ...chart, columnGanttDeadlineId: columnId })
    }
  }

  const handleShowLegend = (showLegend: boolean) => {
    if (chart) {
      setChart({ ...chart, showLegend: showLegend })
    }
  }

  const handleShowValues = (showValues: boolean) => {
    if (chart) {
      setChart({ ...chart, showValues: showValues })
    }
  }

  const handleShowPercentage = (showPercentage: boolean) => {
    if (chart) {
      setChart({ ...chart, showPercentage: showPercentage })
    }
  }

  const handleShowTitle = (showTitle: boolean) => {
    if (chart) {
      setChart({ ...chart, showTitle: showTitle })
    }
  }

  const handleShowCardLabel = (showCardLabel: boolean) => {
    if (chart) {
      setChart({ ...chart, showCardLabel: showCardLabel })
    }
  }

  const handleSelectChartSortAggregate = (sortDirection: sortDirections | null) => {
    if (chart && chart.chartType !== 'gantt') {
      setChart({ ...chart, sortAggregate: sortDirection })
    }
  }

  const handleMaxCategories = (value: string) => {
    if (!chart) return
    const parsed = parseInt(value, 10)
    setChart({ ...chart, maxCategories: parsed >= 1 ? parsed : null })
  }

  const columnValues = spreadsheetData.viewDetails.columns.filter((col) => chart.columnValueIds.includes(col.publicId))
  const areAllNumericColumns = columnValues.every((col) => NUMBER_COLUMN_TYPES.includes(col.kind))
  const areAllDateColumns = columnValues.length > 0 && columnValues.every((col) => DATE_COLUMN_TYPES.includes(col.kind))

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <div className="overflow-y-auto max-h-[500px]">
        <div className="w-full bg-surface-sunken font-bold p-2.5 text-base">Chart Settings</div>

        <div className="w-full p-2.5">
          <Select
            label="Chart Type"
            options={CHART_TYPES}
            onOptionClick={(option) => handleSelectChartType(option as IChartType)}
            optionsSelected={!chart || chart.chartType === null ? [] : [chart.chartType]}
          />
        </div>
        {chart?.chartType !== 'card' && (
          <div className="w-full p-2.5">
            <Select
              label={chart.chartType === 's-curve' ? 'S-Curve Date 1' : 'Label Column / X-Axis'}
              options={spreadsheetData.viewDetails.columns
                .filter((column) => {
                  if (chart && chart.chartType === 's-curve') {
                    return DATE_COLUMN_TYPES.includes(column.kind)
                  } else {
                    return true
                  }
                })
                .map((column) => {
                  return { value: column.publicId, label: column.name }
                })}
              onOptionClick={(option) => handleAddLabelColumn(option)}
              disabled={!chart || chart.chartType === null}
              optionsSelected={!chart || !chart.columnLabelId ? [] : [chart.columnLabelId]}
            />
          </div>
        )}

        {chart?.chartType === 'card' && (
          <div className="w-full p-2.5">
            <Select
              label="Group By (optional)"
              options={spreadsheetData.viewDetails.columns
                .filter((column) => !BLOCK_GROUP_BY.includes(column.kind))
                .map((column) => ({ value: column.publicId, label: column.name }))}
              onOptionClick={(option) => handleAddLabelColumn(option)}
              disabled={!chart || chart.chartType === null}
              optionsSelected={!chart || !chart.columnLabelId ? [] : [chart.columnLabelId]}
              clearable
            />
          </div>
        )}

        {((chart && chart.chartType && chart.chartType !== 'gantt') || !chart || !chart.chartType) && (
          <div className="w-full p-2.5">
            <Select
              label={
                chart.chartType === 's-curve'
                  ? 'S-Curve Date 2'
                  : chart.chartType === 'card'
                    ? 'Value Columns'
                    : 'Value Column / Y-Axis'
              }
              options={spreadsheetData.viewDetails.columns
                .filter((column) => {
                  if (chart && chart.chartType === 's-curve') {
                    return DATE_COLUMN_TYPES.includes(column.kind)
                  } else {
                    return true
                  }
                })
                .map((column) => {
                  return { value: column.publicId, label: column.name }
                })}
              onOptionClick={(option) => handleAddValueColumn(option)}
              disabled={!chart || chart.chartType === null}
              optionsSelected={
                !chart || !chart.columnValueIds || chart.columnValueIds.length === 0 ? [] : chart.columnValueIds
              }
              multiselect={true}
            />
          </div>
        )}

        {chart && chart.chartType && CHARTS_WITH_GROUPING.includes(chart.chartType) && (
          <div className="w-full p-2.5">
            <Select
              label="Groups Column"
              options={spreadsheetData.viewDetails.columns.map((c) => ({ value: c.publicId, label: c.name }))}
              onOptionClick={handleAddStackColumn}
              optionsSelected={chart?.columnStackId ? [chart.columnStackId] : []}
            />
          </div>
        )}

        {chart && chart.chartType && chart.chartType === 'gantt' && (
          <div className="w-full p-2.5">
            <Select
              label="Start Date Column"
              options={spreadsheetData.viewDetails.columns.map((c) => ({ value: c.publicId, label: c.name }))}
              onOptionClick={handleAddGanttStartColumn}
              optionsSelected={chart.columnGanttStartDateId ? [chart.columnGanttStartDateId] : []}
            />
          </div>
        )}

        {chart && chart.chartType && chart.chartType === 'gantt' && (
          <div className="w-full p-2.5">
            <Select
              label="End Date Column"
              options={spreadsheetData.viewDetails.columns.map((c) => ({ value: c.publicId, label: c.name }))}
              onOptionClick={handleAddGanttEndColumn}
              optionsSelected={chart.columnGanttEndDateId ? [chart.columnGanttEndDateId] : []}
            />
          </div>
        )}

        {chart && chart.chartType && chart.chartType === 'gantt' && (
          <div className="w-full p-2.5">
            <Select
              label="Deadline Date Column (optional)"
              options={spreadsheetData.viewDetails.columns.map((c) => ({ value: c.publicId, label: c.name }))}
              onOptionClick={handleAddGanttDeadlineColumn}
              optionsSelected={chart.columnGanttDeadlineId ? [chart.columnGanttDeadlineId] : []}
              clearable
            />
          </div>
        )}

        {chart && chart.chartType !== 'gantt' && chart.chartType !== 's-curve' && (
          <div className="w-full p-2.5">
            <Select
              label="Aggregation Type"
              options={
                areAllNumericColumns
                  ? CHART_AGGREGATES
                  : areAllDateColumns && chart.chartType === 'card'
                    ? CHART_DATE_AGGREGATES
                    : [{ label: 'Count', value: 'count' }]
              }
              onOptionClick={(option) => handleSelectChartAggregate(option as IChartAggregate)}
              optionsSelected={!chart || chart.aggregate === null ? [] : [chart.aggregate]}
            />
          </div>
        )}
        {chart && chart.chartType !== 'gantt' && chart.chartType !== 's-curve' && chart.chartType !== 'card' && (
          <div className="w-full p-[5px]">
            <span className="text-body text-sm">Sort By Aggregate</span>
            <div className="flex-1 ml-[5px] mr-2.5 mt-2.5">
              <div className="flex items-center bg-surface-sunken rounded p-[5px]">
                <div
                  className={`flex items-center justify-center rounded border border-transparent cursor-pointer flex-1
 ${chart.sortAggregate === sortDirections.asc ? 'bg-surface-strong' : ''} mr-2.5 p-[5px]`}
                  onClick={() => handleSelectChartSortAggregate(sortDirections.asc)}
                >
                  1 → 9
                </div>
                <div
                  className={`flex items-center justify-center rounded border border-transparent cursor-pointer flex-1 ${
                    chart.sortAggregate == null ? 'bg-surface-strong' : ''
                  } p-[5px] w-[100px]`}
                  onClick={() => handleSelectChartSortAggregate(null)}
                >
                  OFF
                </div>
                <div
                  className={`flex items-center justify-center rounded border border-transparent cursor-pointer flex-1 ${
                    chart.sortAggregate === sortDirections.desc ? 'bg-surface-strong' : ''
                  } p-[5px] ml-[15px]`}
                  onClick={() => handleSelectChartSortAggregate(sortDirections.desc)}
                >
                  9 → 1
                </div>
              </div>
            </div>
          </div>
        )}

        {chart &&
          chart.chartType &&
          chart.chartType !== 'gantt' &&
          chart.chartType !== 's-curve' &&
          chart.chartType !== 'card' && (
            <div className="w-full p-2.5">
              <label htmlFor="max-categories-input" className="text-muted text-sm mb-[5px]">
                Max Categories
              </label>
              <input
                id="max-categories-input"
                type="number"
                min="1"
                placeholder="Show all"
                className="w-full p-[5px] border border-divider rounded text-sm"
                value={chart.maxCategories ?? ''}
                onChange={(e) => handleMaxCategories(e.target.value)}
              />
            </div>
          )}

        {chart && shouldShowLegend(chart.chartType) && (
          <div className="relative flex items-center rounded text-body select-none truncate p-2.5 h-[50px]">
            <span>Show Legend</span>
            <Toggle
              aria-label="Show Legend"
              toggled={chart && chart.showLegend}
              onToggle={(toggle) => {
                handleShowLegend(toggle)
              }}
            />
          </div>
        )}

        {chart && chart.chartType !== 'gantt' && (
          <div className="relative flex items-center rounded text-body select-none truncate p-2.5 h-[50px]">
            <span>Show Values</span>
            <Toggle
              aria-label="Show Values"
              toggled={chart && chart.showValues}
              onToggle={(toggle) => {
                handleShowValues(toggle)
              }}
            />
          </div>
        )}

        {chart && chart.chartType !== 'gantt' && (
          <div className="relative flex items-center rounded text-body select-none truncate p-2.5 h-[50px]">
            <span>Show Percentage</span>
            <Toggle
              aria-label="Show Percentage"
              toggled={chart && chart.showPercentage}
              onToggle={(toggle) => {
                handleShowPercentage(toggle)
              }}
            />
          </div>
        )}

        {chart && chart.chartType === 'card' && (
          <div className="relative flex items-center rounded text-body select-none truncate p-2.5 h-[50px]">
            <span>Show Card Labels</span>
            <Toggle
              aria-label="Show Card Labels"
              toggled={chart && chart.showCardLabel}
              onToggle={(toggle) => {
                handleShowCardLabel(toggle)
              }}
            />
          </div>
        )}

        <div className="relative flex items-center rounded text-body select-none truncate p-2.5 h-[50px]">
          <span>Show Title</span>
          <Toggle
            aria-label="Show Title"
            toggled={chart && chart.showTitle}
            onToggle={(toggle) => {
              handleShowTitle(toggle)
            }}
          />
        </div>
      </div>
    </Menu>
  )
}

export default ChartMenu
